// frontend/src/features/workspace/api/use-projects.ts
import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import type { Capability } from '@shared/collaboration/access'

export interface Project {
  id: string
  tenantId: string
  createdBy: string
  name: string
  description: string | null
  modelCount: number
  createdAt: string
  updatedAt: string
  capabilities?: Capability[] | undefined
}

export function useProjects(): UseQueryResult<Project[]> {
  const api = useApi()

  return useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: (): Promise<Array<Project>> => api.get<Project[]>('/projects'),
  })
}

export function useProject(id: string | undefined): UseQueryResult<Project> {
  const api = useApi()

  return useQuery<Project>({
    queryKey: ['projects', id],
    queryFn: (): Promise<Project> => api.get<Project>(`/projects/${id}`),
    enabled: !!id,
  })
}

export function useCreateProject(): UseMutationResult<Project, Error, { name: string; description?: string }> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<Project, Error, { name: string; description?: string }>({
    mutationFn: (params): Promise<Project> => api.post<Project>('/projects', params),
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateProject(): UseMutationResult<Project, Error, { id: string; name?: string; description?: string | null }> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<Project, Error, { id: string; name?: string; description?: string | null }>({
    mutationFn: ({ id, ...params }): Promise<Project> => api.put<Project>(`/projects/${id}`, params),
    onSuccess: (_, variables): void => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteProject(): UseMutationResult<void, Error, string> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (id): Promise<void> => api.delete(`/projects/${id}`),
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
